import React, { useEffect, useState } from 'react';
import RialSymbol from '../../components/RialSymbol';
import { Box, Card, CardContent, Typography, Button, Chip } from '@mui/material';
import {
  ShoppingBag as OrderIcon,
  Favorite as FavoriteIcon,
  LocalOffer as OfferIcon,
  Star as EcoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import customerService from '../../services/customer.service';
import LoadingAnimation from '../../components/LoadingAnimation';

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch customer profile with stats
        const profile = await customerService.getProfile();
        setStats({
          totalOrders: profile.totalOrders || 0,
          totalSpent: profile.totalSpent || 0,
          favoriteBusinesses: profile.favorites?.length || 0,
          foodRescued: profile.totalOrders || 0,
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
        setStats({
          totalOrders: 0,
          totalSpent: 0,
          favoriteBusinesses: 0,
          foodRescued: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <LoadingAnimation message="Loading your dashboard..." subtitle="Welcome back!" />;
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Welcome Back! 👋
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Discover amazing surprise boxes and help reduce food waste
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ bgcolor: 'primary.main', borderRadius: 2, p: 1.5, mr: 2 }}>
                <OrderIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {stats?.totalOrders || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Orders
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ bgcolor: 'secondary.main', borderRadius: 2, p: 1.5, mr: 2 }}>
                <FavoriteIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {stats?.favoriteBusinesses || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Favorites
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ bgcolor: 'warning.main', borderRadius: 2, p: 1.5, mr: 2 }}>
                <OfferIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  <RialSymbol /> {stats?.totalSpent || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Saved
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ bgcolor: 'success.main', borderRadius: 2, p: 1.5, mr: 2 }}>
                <EcoIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {stats?.foodRescued || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Meals Rescued
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Quick Actions */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Quick Actions
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<OrderIcon />}
              onClick={() => navigate('/surprise-boxes')}
            >
              Browse Surprise Boxes
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<FavoriteIcon />}
              onClick={() => navigate('/surprise-boxes')}
            >
              View Favorites
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/orders')}
            >
              Order History
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Impact Card */}
      <Card sx={{ background: 'linear-gradient(135deg, #0B6E4F 0%, #B1D65D 100%)', color: 'white' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Your Environmental Impact 🌍
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            By rescuing {stats?.foodRescued || 0} meals, you've helped reduce food waste and support local businesses!
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Chip
              label={`${stats?.foodRescued || 0} Meals Saved`}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
            />
            <Chip
              label="Hero Status 🦸"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
